import React, { useState, useEffect, useRef, useCallback } from "react";

import {
  addMessageToChat,
  clearMessagesForChat,
  fetchWhatsappChats,
  sendTextMessageToNumber,
} from "../../../store/slices/whatsappDataSlice";
import { RootDispatch, RootState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import socket from "../../../utils/socketConnect.utils";
import ChatBubble from "./ChatBubble";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import useOutsideClick from "../../Assets/useOutsideClick";
import { PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface ChatProps {
  onBackClick?: () => void;
}

const ChatWindow: React.FC<ChatProps> = ({ onBackClick }) => {
  const loading = useSelector((state: RootState) => state.whatsAppData.loading);
  const [newMessage, setNewMessage] = useState("");
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const chatBubbleLoading = useSelector(
    (state: RootState) => state.whatsAppData.chatBubbleLoading
  );
  const chatId = useSelector(
    (state: RootState) => state.whatsAppData.selectedChatId
  );

  const messagesByChatId = useSelector(
    (state: RootState) => state.whatsAppData.messagesByChatId
  );
  const messages = chatId ? messagesByChatId[chatId] : undefined;

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const emojiWrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const prevScrollHeightRef = useRef<number>(0);
  const isScrollingToBottomRef = useRef<boolean>(false);

  const dispatch: RootDispatch = useDispatch();

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    if (messagesEndRef.current && messagesContainerRef.current) {
      isScrollingToBottomRef.current = true;
      messagesEndRef.current.scrollIntoView({ behavior });

      setTimeout(() => {
        isScrollingToBottomRef.current = false;
      }, 500);
    }
  }, []);

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  };

  useOutsideClick(emojiWrapperRef, () => {
    if (showEmojiPicker) setShowEmojiPicker(false);
  });

  useEffect(() => {
    if (chatId) {
      dispatch(clearMessagesForChat(chatId));
      setPage(1);
      scrollToBottom("smooth");
      setHasMoreMessages(true);
      setIsInitialLoad(true);
      setIsLoadingMore(false);
    }
  }, [chatId, scrollToBottom, dispatch]);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (
      !container ||
      isLoadingMore ||
      !hasMoreMessages ||
      isScrollingToBottomRef.current
    )
      return;
    if (container.scrollTop === 0) {
      setIsLoadingMore(true);
      setPage((prevPage) => prevPage + 1);
    } else {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [
    handleScroll,
    messagesContainerRef,
    isLoadingMore,
    hasMoreMessages,
    isScrollingToBottomRef,
    chatId,
    dispatch,
  ]);

  const fetchMessages = useCallback(async () => {
    if (!chatId || (!hasMoreMessages && page > 1)) return;

    try {
      const data = await dispatch(
        fetchWhatsappChats({ chatId, page })
      ).unwrap();

      if (data.hasMore === false) {
        console.warn("No messages found for this chat.");
        setHasMoreMessages(false);
        setIsLoadingMore(false);
        return;
      }

      if (messagesContainerRef.current) {
        prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
      }

      setIsLoadingMore(false);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      setIsLoadingMore(false);
      setHasMoreMessages(false);
    }
  }, [chatId, page, hasMoreMessages, dispatch]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (page === 1 && !isInitialLoad) {
      scrollToBottom("auto");
    }
  }, [messages, page, isInitialLoad, scrollToBottom]);

  useEffect(() => {
    if (!socket.connected) socket.connect();

    const handleOpenMessage = (msg: any) => {
      if (msg?.latestMessage?.chatId === chatId) {
        socket.emit("isOpen", chatId);
        dispatch(
          addMessageToChat({ chatId, message: msg?.latestMessage?._doc })
        );
        setTimeout(() => scrollToBottom(), 100);
      }
    };

    const handleNewMessage = (msg: any) => {
      dispatch(addMessageToChat({ chatId, message: msg }));
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    };

    socket.on("open-" + chatId, handleOpenMessage);
    socket.on("new-message", handleNewMessage);

    return () => {
      socket.off("open-" + chatId);
      socket.off("new-message");
    };
  }, [chatId, scrollToBottom, messages, dispatch, messagesByChatId]);

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const validFiles = filesArray.filter((file) => {
        const fileType = file.type.split("/")[0];
        const isValidType = ["image", "video", "application"].includes(
          fileType
        );
        const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB

        if (!isValidType) {
          toast.error(
            `Invalid file type: ${file.name}. Only images, videos, and documents are allowed.`
          );
          return false;
        }

        if (!isValidSize) {
          toast.error(`File ${file.name} exceeds the 10MB limit.`);
          return false;
        }

        return true;
      });

      setAttachments((prev) => [...prev, ...validFiles]);

      // Create preview URLs for images
      const imageUrls = validFiles
        .filter((file) => file.type.startsWith("image/"))
        .map((file) => URL.createObjectURL(file));
      setPreviewUrls((prev) => [...prev, ...imageUrls]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));

    // Revoke the object URL if it's an image
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
      setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSendTextMessage = async (sentTo: string, message: string) => {
    scrollToBottom("instant" as ScrollBehavior);
    if (!sentTo || (!message.trim() && attachments.length === 0)) {
      toast.error("Please enter a message or attach a file");
      return;
    }
    setNewMessage("");
    try {
      const formData = new FormData();
      formData.append("sentTo", sentTo);
      formData.append("message", message || "");

      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      await dispatch(sendTextMessageToNumber(formData)).unwrap();

      setAttachments([]);
      setPreviewUrls([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setTimeout(() => scrollToBottom(), 100);
    } catch (error: any) {
      console.error("Failed to send message:", error);
      toast.error(error.message || "Failed to send message");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      messageInputRef.current?.focus();
    }, 300);

    return () => clearTimeout(timer);
  }, [chatId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement !== messageInputRef.current) {
        e.preventDefault();
        messageInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="flex flex-col h-full relative">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-gray-200 bg-gray-50 relative z-10">
        {onBackClick && (
          <button
            onClick={onBackClick}
            className="mr-3 p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
            aria-label="Back to chat list"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}
        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-white font-medium">
          {messages?.receiver?.charAt(0)?.toUpperCase()}
        </div>
        <div className="ml-3 flex-1 truncate">
          <div className="font-medium truncate">{messages?.receiver}</div>
        </div>
      </div>

      {(isLoadingMore || (loading && isInitialLoad)) && (
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-20 bg-white shadow-lg rounded-full px-4 py-2 flex items-center space-x-2 border">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-500 border-t-transparent" />
          <span className="text-sm text-gray-600 font-medium">
            {isInitialLoad
              ? "Loading messages..."
              : "Loading older messages..."}
          </span>
        </div>
      )}

      <div
        ref={messagesContainerRef}
        className="flex-1 p-4 overflow-y-auto bg-gray-100 space-y-3 scroll-smooth"
        style={{
          scrollBehavior: "smooth",
          overflowAnchor: "none",
        }}
      >
        {messages?.chats?.map((msg, index) => {
          return (
            <ChatBubble
              key={`${msg?._id || index}-${msg?.createdAt}`}
              msg={msg}
            />
          );
        })}

        {chatBubbleLoading && (
          <div className="flex justify-end">
            <div className="max-w-xs bg-green-100 text-green-800 px-4 py-2 rounded-2xl text-sm shadow-md animate-pulse">
              ...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Attachment previews */}

      {(previewUrls.length > 0 || attachments.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-2">
          {previewUrls.map((url, index) => (
            <div key={index} className="relative">
              <img
                src={url}
                alt={`Preview ${index}`}
                className="h-20 w-20 object-cover rounded-lg"
              />
              <button
                onClick={() => removeAttachment(index)}
                className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
              >
                <XMarkIcon className="h-4 w-4 text-white" />
              </button>
            </div>
          ))}
          {attachments
            .filter((f) => !f.type.startsWith("image/"))
            .map((file, index) => (
              <div
                key={index}
                className="relative bg-gray-100 rounded-lg p-2 flex items-center"
              >
                <PaperClipIcon className="h-4 w-4 text-gray-500 mr-1" />
                <span className="text-sm truncate max-w-xs">{file.name}</span>
                <button
                  onClick={() => removeAttachment(index)}
                  className="ml-1 text-red-500"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
        </div>
      )}
      {/* Message Input */}
      <div className="border-t border-gray-200 p-3 flex items-center bg-white relative">
        {/* wrap both button + picker */}
        <div ref={emojiWrapperRef} className="relative">
          <button
            onClick={() => setShowEmojiPicker((v) => !v)}
            className="mr-2 p-1 rounded-2xl shadow hover:shadow-lg transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
            aria-label="Toggle emoji picker"
          >
            <svg
              className="w-6 h-6 text-gray-500"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 2C6.477 2 2 5.82 2 10.5c0 2.928 1.679 5.523 4.313 7.165L4 22l4.618-1.293A9.951 9.951 0 0012 22c5.523 0 10-3.82 10-9.5S17.523 2 12 2zm0 17c-1.938 0-3.754-.524-5.313-1.438l-.813-.438.25-.875A8.004 8.004 0 014 10.5C4 6.91 7.134 4 12 4s8 2.91 8 6.5-3.134 6.5-8 6.5zm-3-7a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm6 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm-3 4a4.5 4.5 0 01-4.243-3H9.5a3 3 0 006 0h1.743A4.5 4.5 0 0112 16z" />
            </svg>
          </button>

          {showEmojiPicker && (
            <div className="absolute bottom-16 left-0 z-50">
              <EmojiPicker
                onEmojiClick={onEmojiClick}
                width={300}
                height={350}
                previewConfig={{ showPreview: false }}
                skinTonesDisabled
                searchDisabled
              />{" "}
            </div>
          )}
        </div>
        {/* Attachment button */}
        <label className="mr-2 p-1 rounded-2xl shadow hover:shadow-lg transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 cursor-pointer">
          <PaperClipIcon className="h-6 w-6 text-gray-500" />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleAttachmentChange}
            className="hidden"
            accept="image/*,video/*,.pdf,.doc,.docx"
          />
        </label>
        <input
          ref={messageInputRef}
          type="text"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter" && newMessage.trim()) {
              handleSendTextMessage(messages?.receiver!, newMessage);
            }
          }}
          className="flex-1 border border-gray-300 rounded-full py-2 px-4 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
          disabled={!messages?.receiver}
        />

        <button
          onClick={() => handleSendTextMessage(messages?.receiver!, newMessage)}
          disabled={
            (!newMessage.trim() && attachments.length === 0) ||
            !messages?.receiver
          }
          className="ml-3 bg-green-500 text-white rounded-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none hover:bg-green-600 focus:ring-2 focus:ring-green-300 transition-all transform hover:scale-105 active:scale-95"
          aria-label="Send message"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatWindow;
